import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useLocation, useNavigate } from "react-router-dom";
import { useTenantProfileCreate } from "@/hooks/useAdminDashboardService";
import { toast } from "react-toastify";
import type { StepData } from "@/types/companyProfile";
import CompanyBasics from "../compnayprofile/CompanyBasics";
import BusinessEcosystem from "../compnayprofile/BusinessEcosystem";
import MarketLandscape from "../compnayprofile/MarketLandscape";
import type { TenantProfilePayload } from "@/types/tenantService";
import { useTenantById } from "@/hooks/useTenantDashboardService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

// const ReviewConfirm = () => {
//   return (
//     <div className="text-white space-y-2">
//       <p>Review your details before submitting.</p>
//     </div>
//   );
// };

const ReviewConfirm: React.FC<{ formData: TenantProfilePayload }> = ({
  formData,
}) => {
  const renderList = (label: string, values: string[]) => (
    <div className="mb-4">
      <h4 className="text-md font-semibold text-white mb-1">{label}:</h4>
      {values.length > 0 ? (
        <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
          {values.map((val, idx) => (
            <li key={idx}>{val}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-400 italic">No data provided</p>
      )}
    </div>
  );

  return (
    <div className="text-white space-y-4">
      <h3 className="text-lg font-semibold">Review & Confirm</h3>

      {renderList("Competitors", formData.competitors)}
      {renderList("Unique Selling Points", formData.uniqueSellingPoints)}
      {renderList("Target Industries", formData.targetIndustries)}
      {renderList("Partners", formData.partners)}
      {renderList("Sales Methodology", formData.salesMethodology)}
      {renderList("Products & Services", formData.productsServices)}
      {renderList("Value Proposition", formData.valueProposition)}

      {formData.whoweare && (
        <div>
          <h4 className="text-md font-semibold mb-1 text-white">Who We Are:</h4>
          <p className="text-sm text-gray-300 whitespace-pre-line">
            {formData.whoweare}
          </p>
        </div>
      )}

      {formData.reviewText && (
        <div>
          <h4 className="text-md font-semibold mb-1 text-white">
            Review Summary:
          </h4>
          <p className="text-sm text-gray-300 whitespace-pre-line">
            {formData.reviewText}
          </p>
        </div>
      )}
    </div>
  );
};

const CompanyProfile: React.FC = () => {
  const { isDark } = useDashboardTheme();
  const tenantId = localStorage.getItem("tenantId") || "";
  const { data } = useTenantById({
    tenantId,
    query: {
      include: "tenantProfile,usage_log,planMaster",
    },
  });

  const [activeStep, setActiveStep] = React.useState<number>(0);
  const [errorSteps, setErrorSteps] = React.useState<number[]>([]);
  const [stepValidity, setStepValidity] = React.useState<boolean[]>([
    false,
    false,
    false,
    true,
  ]);
  const [formErrors, setFormErrors] = React.useState<Record<string, string>>(
    {},
  );

  const [formData, setFormData] = React.useState<TenantProfilePayload>({
    competitors: [],
    uniqueSellingPoints: [],
    targetIndustries: [],
    partners: [],
    salesMethodology: [],
    reviewText: "",
    whoweare: "",
    valueProposition: [],
    productsServices: [],
  });

  // ✅ Load initial profile data
  React.useEffect(() => {
    if (data?.tenantProfile) {
      const latestData = data.tenantProfile;
      setFormData({
        competitors: latestData?.competitors || [],
        uniqueSellingPoints: latestData?.uniqueSellingPoints || [],
        targetIndustries: latestData?.targetIndustries || [],
        partners: latestData?.partners || [],
        salesMethodology: latestData?.salesMethodology || [],
        reviewText: latestData?.reviewText || "",
        whoweare: latestData?.about || "",
        valueProposition: latestData?.valueProposition || [],
        productsServices: latestData?.productsServices || [],
      });
    }
  }, [data]);

  const location = useLocation();
  const navigate = useNavigate();
  const { mutate } = useTenantProfileCreate();

  // ✅ Step validation logic
  const validateStep = React.useCallback(
    (index: number) => {
      const errors: Record<string, string> = {};

      switch (index) {
        case 0:
          if (!formData.whoweare?.trim())
            errors.whoweare = "About is required.";
          if (formData.productsServices.length === 0)
            errors.productsServices = "At least one product is required.";
          if (formData.valueProposition.length === 0)
            errors.valueProposition =
              "At least one value proposition is required.";
          break;

        case 1:
          if (formData.competitors.length === 0)
            errors.competitors = "Competitors are required.";
          if (formData.uniqueSellingPoints.length === 0)
            errors.uniqueSellingPoints = "Unique selling points are required.";
          if (formData.targetIndustries.length === 0)
            errors.targetIndustries = "Target industries are required.";
          break;

        case 2:
          if (formData.partners.length === 0)
            errors.partners = "Partners are required.";
          if (formData.salesMethodology.length === 0)
            errors.salesMethodology = "Sales methodology is required.";
          break;
      }

      // setFormErrors(errors);
      const isValid = Object.keys(errors).length === 0;

      setStepValidity((prev) => {
        const updated = [...prev];
        updated[index] = isValid;
        return updated;
      });

      return isValid;
    },
    [formData],
  );

  // ✅ Re-validate when step or data changes
  React.useEffect(() => {
    validateStep(activeStep);
  }, [formData, activeStep, validateStep]);
  React.useEffect(() => {
    const isValid = validateStep(activeStep);
    setStepValidity((prev) => {
      const updated = [...prev];
      updated[activeStep] = isValid;
      return updated;
    });
  }, [formData]);

  const steps: StepData[] = [
    {
      label: "Company Basics",
      description: (
        <CompanyBasics
          formData={formData}
          setFormData={setFormData}
          data={location?.state}
          errors={formErrors}
          setFormErrors={setFormErrors}
        />
      ),
    },
    {
      label: "Market Landscape",
      description: (
        <MarketLandscape
          formData={formData}
          setFormData={setFormData}
          data={location?.state}
          errors={formErrors}
          setFormErrors={setFormErrors}
        />
      ),
    },
    {
      label: "Business Ecosystem",
      description: (
        <BusinessEcosystem
          formData={formData}
          setFormData={setFormData}
          data={location?.state}
          errors={formErrors}
          setFormErrors={setFormErrors}
        />
      ),
    },
    // {
    //   label: "Review & Confirm",
    //   description: <ReviewConfirm />,
    // },
    {
      label: "Review & Confirm",
      description: <ReviewConfirm formData={formData} />,
    },
  ];

  const handleNext = () => {
    const isValid = validateStep(activeStep);
    if (!isValid) {
      if (!errorSteps.includes(activeStep)) {
        setErrorSteps((prev) => [...prev, activeStep]);
      }
      return;
    }
    setErrorSteps((prev) => prev.filter((step) => step !== activeStep));
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleReset = () => setActiveStep(0);

  const handleSubmit = () => {
    const validations = [0, 1, 2].map((i) => validateStep(i));
    const firstInvalidIndex = validations.findIndex((v) => !v);

    if (firstInvalidIndex !== -1) {
      setActiveStep(firstInvalidIndex);
      setErrorSteps((prev) =>
        Array.from(new Set([...prev, firstInvalidIndex])),
      );
      return;
    }

    const payload = {
      id: data?.tenantProfile?.id || "",
      about: formData.whoweare,
      productsServices: formData.productsServices,
      tenantId,
      valueProposition: formData.valueProposition,
      competitors: formData.competitors,
      uniqueSellingPoints: formData.uniqueSellingPoints,
      partners: formData.partners,
      targetIndustries: formData.targetIndustries,
      salesMethodology: formData.salesMethodology,
      reviewText: formData.reviewText,
    };

    mutate(payload, {
      onSuccess: () => {
        navigate("/company-profile-info");
        toast.success("Profile updated successfully!");
      },
      onError: (error) => {
        console.error("Profile Update Failed:", error);
        toast.error("Failed to update profile.");
      },
    });
  };

  return (
    <main className={"flex-1 p-4 md:p-6 mt-20 " + (isDark ? "" : "light-theme-dashboard")}>
      <div className="max-w-6xl mx-auto bg-[#211F2F] manual-profile-card rounded-2xl p-6 shadow-md">
        <Box sx={{ maxWidth: 800 }}>
          {/* Back Button */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: "#B8F890",
                textTransform: "none",
                "&:hover": { backgroundColor: "#2f2c3f" },
              }}
            >
              Back
            </Button>
          </Box>

          {/* Stepper */}
          <Stepper
            activeStep={activeStep}
            orientation="vertical"
            sx={{
              ".MuiStepIcon-root": { color: "#B8F890" },
              ".MuiStepIcon-text": { fill: "#211F2F", fontWeight: "bold" },
              ".Mui-active .MuiStepIcon-root": { color: "#501b99" },
              ".Mui-active .MuiStepIcon-text": { fill: "#ffffff" },
              ".MuiStepIcon-root.Mui-completed": { color: "#000000" },
              ".MuiStepLabel-label.Mui-completed": { color: "black" },
              ".MuiStepIcon-root.Mui-completed path": { fill: "white" },
              ".MuiStepLabel-label": { color: "#B8F890" },
            }}
          >
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  onClick={() => {
                    if (validateStep(activeStep)) setActiveStep(index);
                  }}
                  StepIconProps={{
                    sx: errorSteps.includes(index) ? { color: "red" } : {},
                  }}
                >
                  <h1 className="text-1xl font-semibold poppins text-white">
                    {step.label}
                  </h1>
                </StepLabel>

                <StepContent>
                  <Box sx={{ color: "white" }}>{step.description}</Box>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() =>
                        index === steps.length - 1
                          ? handleSubmit()
                          : handleNext()
                      }
                      disabled={!stepValidity[activeStep]}
                      sx={{
                        mt: 1,
                        mr: 1,
                        backgroundColor: "#B8F890",
                        color: "#211F2F",
                        "&:hover": { backgroundColor: "#A0E275" },
                      }}
                    >
                      {index === steps.length - 1 ? "Finish" : "Continue"}
                    </Button>

                    {index !== 0 && (
                      <Button
                        onClick={handleBack}
                        sx={{
                          mt: 1,
                          mr: 1,
                          color: "#B8F890",
                          border: "1px solid #B8F890",
                          "&:hover": {
                            backgroundColor: "#B8F890",
                            color: "#211F2F",
                          },
                        }}
                      >
                        Back
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {/* Finish screen */}
          {activeStep === steps.length && (
            <Paper
              square
              elevation={0}
              sx={{ p: 3, backgroundColor: "#211F2F", color: "#FFFFFF" }}
            >
              <Typography>All steps are completed!</Typography>
              <Button
                onClick={handleReset}
                sx={{
                  mt: 1,
                  mr: 1,
                  backgroundColor: "#B8F890",
                  color: "#211F2F",
                  "&:hover": { backgroundColor: "#A0E275" },
                }}
              >
                Reset
              </Button>
            </Paper>
          )}
        </Box>
      </div>
    </main>
  );
};

export default CompanyProfile;
